import { createFileRoute, Link } from "@tanstack/react-router";
import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import { ConnectAiDocs } from "@/components/setup/connect-ai-docs";

export const Route = createFileRoute("/docs/mcp")({
	component: Mcp,
});

function Mcp() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<h1 className="text-2xl font-medium tracking-tight">MCP overview</h1>
				<p className="text-sm text-muted-foreground">
					Control AgentReacher from any MCP-compatible AI assistant.
				</p>
			</div>

			<ConnectAiDocs />

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<h2 className="text-base font-medium text-foreground">What is MCP?</h2>
				<p>
					MCP (Model Context Protocol) is an open protocol that lets AI
					assistants like Claude, Cursor, or Windsurf interact directly with
					external tools. AgentReacher implements MCP so you can manage your
					entire social media workflow from inside your AI assistant. No
					dashboard needed.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					How it works
				</h2>
				<p>
					AgentReacher exposes a set of tools through the MCP protocol. When you
					connect your MCP client, those tools become available inside your AI
					assistant. You talk to your assistant in natural language and it calls
					the right AgentReacher tools on your behalf.
				</p>
				<p>
					The web dashboard and MCP connect to the same backend. Everything you
					do through MCP shows up in the dashboard, and vice versa.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					What you can ask your AI to do
				</h2>
				<p>
					With MCP connected, your AI assistant can handle your full social
					media workflow. Here are some examples:
				</p>
				<Suggestions title="Example prompts">
					<Suggestion suggestion="Create a post about our new feature for X, LinkedIn, and Instagram." />
					<Suggestion suggestion="Schedule it for tomorrow at 9am." />
					<Suggestion suggestion="Show me everything scheduled for this week." />
					<Suggestion suggestion="Update the draft to use a different headline." />
					<Suggestion suggestion="Delete the post I created yesterday." />
					<Suggestion suggestion="What social accounts are connected?" />
					<Suggestion suggestion="Disconnect Bluesky from this workspace." />
					<Suggestion suggestion="Invite maria@example.com to the workspace." />
					<Suggestion suggestion="Switch to my other workspace." />
					<Suggestion suggestion="Create a new workspace called Summer Campaign." />
					<Suggestion suggestion="Publish the draft about the conference right now." />
				</Suggestions>
				<p>
					Your assistant figures out which tool to call and what parameters to
					pass. You just describe what you want.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Setting it up
				</h2>
				<ol className="list-decimal pl-5 space-y-2">
					<li>Use the install option above for Claude or ChatGPT.</li>
					<li>
						On first use, AgentReacher opens a browser so you can sign in and
						authorize the connection. You do not need to create an API key
						manually unless you want to.
					</li>
					<li>
						The AgentReacher tools appear in your assistant once the connection
						is approved. No restart required for Claude Code or ChatGPT.
					</li>
				</ol>

				<h2 className="text-base font-medium text-foreground pt-2">
					Workspaces and MCP
				</h2>
				<p>
					Your MCP session is scoped to a workspace. You can switch workspaces,
					create new ones, or delete them, all from your AI assistant. Each
					workspace has its own connections, posts, and members.
				</p>
			</div>

			<div className="flex items-center gap-4 text-sm">
				<Link
					to="/docs/connecting-accounts"
					className="text-muted-foreground hover:text-foreground transition-colors"
				>
					&larr; Connecting accounts
				</Link>
				<Link
					to="/docs/mcp-tools"
					className="text-primary hover:text-primary/80 transition-colors ml-auto"
				>
					MCP tools reference &rarr;
				</Link>
			</div>
		</article>
	);
}
